import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';

// Convert base64 to Uint8Array for VAPID key
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function usePushNotifications() {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [permission, setPermission] = useState('default');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Check if push notifications are supported
  useEffect(() => {
    const checkSupport = () => {
      const supported = 'serviceWorker' in navigator && 
                       'PushManager' in window && 
                       'Notification' in window;
      setIsSupported(supported);
      
      if (supported) {
        setPermission(Notification.permission);
      }
    };
    
    checkSupport();
  }, []);

  // Check current subscription status
  useEffect(() => {
    const checkSubscription = async () => {
      if (!isSupported) return;
      
      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        setIsSubscribed(!!subscription);
      } catch (err) {
        console.error('Error checking subscription:', err);
      }
    };
    
    checkSubscription();
  }, [isSupported]);

  // Register service worker
  const registerServiceWorker = useCallback(async () => {
    if (!('serviceWorker' in navigator)) {
      throw new Error('Service Worker non supporté');
    }
    
    try {
      const registration = await navigator.serviceWorker.register('/sw.js');
      console.log('Service Worker registered:', registration);
      return registration;
    } catch (err) {
      console.error('Service Worker registration failed:', err);
      throw err;
    }
  }, []);

  // Subscribe to push notifications
  const subscribe = useCallback(async () => {
    if (!isSupported) {
      setError('Les notifications push ne sont pas supportées sur ce navigateur');
      return false;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Request notification permission
      const notifPermission = await Notification.requestPermission();
      setPermission(notifPermission);
      
      if (notifPermission !== 'granted') {
        setError('Permission refusée pour les notifications');
        setLoading(false);
        return false;
      }
      
      // Register service worker
      await registerServiceWorker();
      const registration = await navigator.serviceWorker.ready;
      
      // Get VAPID public key from server
      const { data: vapidData } = await api.get('/push/vapid-public-key');
      const vapidPublicKey = vapidData.publicKey;
      
      if (!vapidPublicKey) {
        throw new Error('Clé VAPID non disponible');
      }
      
      // Subscribe to push manager
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });
      
      // Send subscription to server
      const subscriptionJson = subscription.toJSON();
      await api.post('/push/subscribe', {
        endpoint: subscriptionJson.endpoint,
        keys: subscriptionJson.keys
      });
      
      setIsSubscribed(true);
      setLoading(false);
      return true;
      
    } catch (err) {
      console.error('Subscription error:', err);
      setError(err.message || 'Erreur lors de l\'activation des notifications');
      setLoading(false);
      return false;
    }
  }, [isSupported, registerServiceWorker]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        // Unsubscribe from push manager
        await subscription.unsubscribe();
        
        // Remove subscription from server
        const subscriptionJson = subscription.toJSON();
        await api.delete('/push/unsubscribe', {
          data: {
            endpoint: subscriptionJson.endpoint,
            keys: subscriptionJson.keys
          }
        });
      }
      
      setIsSubscribed(false);
      setLoading(false);
      return true;
      
    } catch (err) {
      console.error('Unsubscribe error:', err);
      setError(err.message || 'Erreur lors de la désactivation');
      setLoading(false);
      return false;
    }
  }, []);

  // Send test notification
  const sendTestNotification = useCallback(async () => {
    try {
      await api.post('/push/test');
      return true;
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors du test');
      return false;
    }
  }, []);

  return {
    isSupported,
    isSubscribed,
    permission,
    loading,
    error,
    subscribe,
    unsubscribe,
    sendTestNotification
  };
}
